<template>
    <div class="dashboard-body-futuristic">
        <main class="futuristic-container">

            <!-- Sidebar -->
            <AppSidebar activeItem="files" />

            <!-- Main Content Area -->
            <section class="dashboard-card-futuristic dashboard-card-scrollable">
                <div class="file-list-wrapper">

                    <div class="bl-header">
                        <div class="bl-heading">
                            <h1 class="bl-title">Bids</h1>
                        </div>

                        <!-- Viewers can't upload, so they don't get the button. -->
                        <button v-if="isAdminUser" type="button" class="bl-upload-btn" @click="fileUploadPage">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="2.2" aria-hidden="true">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="17 8 12 3 7 8" />
                                <line x1="12" y1="3" x2="12" y2="15" />
                            </svg>
                            Upload PDFs
                        </button>
                    </div>

                    <!-- Status tabs. The active tab lives in ?tab= so Back
                         returns to it. -->
                    <div class="bl-tabs-block">
                        <div class="bl-tabs" role="tablist" aria-label="Bid status">
                            <button v-for="(tab, index) in tabs" :key="tab.key" type="button" role="tab"
                                :id="'bl-tab-' + tab.key" :aria-selected="activeTab === tab.key"
                                aria-controls="bl-tabpanel" :tabindex="activeTab === tab.key ? 0 : -1"
                                :class="['bl-tab', { active: activeTab === tab.key }]"
                                @click="selectTab(tab.key)" @keydown="onTabKeydown($event, index)">
                                {{ tab.label }}
                            </button>
                        </div>
                        <p class="bl-tab-hint">{{ activeHint }}</p>
                    </div>

                    <div class="search-sort-bar">
                        <div class="search-container">
                            <svg class="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" stroke-width="2" aria-hidden="true">
                                <circle cx="11" cy="11" r="8" />
                                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                            </svg>
                            <input type="search" placeholder="Search bids" aria-label="Search bids"
                                class="search-bar" v-model="searchQuery">
                        </div>

                        <div class="controls-container">
                            <!-- Category Filter (bids only; To Upload rows are bookmarks) -->
                            <div v-if="activeTab !== 'PENDING'" class="sort-wrapper category-filter-wrapper">
                                <label for="categorySelect" class="category-label">Category:</label>
                                <select id="categorySelect" v-model="selectedCategory" class="category-select">
                                    <option value="">All Categories</option>
                                    <option v-for="cat in categories" :key="cat.id" :value="cat.id">
                                        {{ cat.name }}
                                    </option>
                                </select>
                            </div>

                            <div class="sort-wrapper">
                                <label for="sortSelect" class="sort-label">Sort by:</label>
                                <select id="sortSelect" v-model="sortBy" class="sort-select">
                                    <option value="newest">Latest Deadline First</option>
                                    <option value="oldest">Earliest Deadline First</option>
                                    <option value="name_asc">Name (A–Z)</option>
                                    <option value="name_desc">Name (Z–A)</option>
                                </select>
                            </div>

                            <!-- View Toggles -->
                            <div class="view-toggles" role="group" aria-label="Layout">
                                <button type="button" :class="['btn-toggle', { active: viewMode === 'grid' }]"
                                    @click="viewMode = 'grid'" aria-label="Grid view" title="Grid view"
                                    :aria-pressed="viewMode === 'grid'">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                        stroke-width="2" aria-hidden="true">
                                        <rect x="3" y="3" width="7" height="7" />
                                        <rect x="14" y="3" width="7" height="7" />
                                        <rect x="14" y="14" width="7" height="7" />
                                        <rect x="3" y="14" width="7" height="7" />
                                    </svg>
                                </button>
                                <button type="button" :class="['btn-toggle', { active: viewMode === 'list' }]"
                                    @click="viewMode = 'list'" aria-label="List view" title="List view"
                                    :aria-pressed="viewMode === 'list'">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                        stroke-width="2" aria-hidden="true">
                                        <line x1="8" y1="6" x2="21" y2="6" />
                                        <line x1="8" y1="12" x2="21" y2="12" />
                                        <line x1="8" y1="18" x2="21" y2="18" />
                                        <line x1="3" y1="6" x2="3.01" y2="6" />
                                        <line x1="3" y1="12" x2="3.01" y2="12" />
                                        <line x1="3" y1="18" x2="3.01" y2="18" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div id="bl-tabpanel" role="tabpanel" :aria-labelledby="'bl-tab-' + activeTab">

                        <!-- Loading -->
                        <div v-if="loading" class="bl-loading" role="status">
                            <p class="bl-loading-text">
                                <span class="bl-spinner" aria-hidden="true"></span>
                                Loading bids…
                            </p>
                            <div class="bl-skeleton" aria-hidden="true">
                                <div v-for="n in 5" :key="n" class="bl-skeleton-row">
                                    <span class="bl-skeleton-icon"></span>
                                    <span class="bl-skeleton-bar wide"></span>
                                    <span class="bl-skeleton-bar"></span>
                                    <span class="bl-skeleton-bar short"></span>
                                </div>
                            </div>
                        </div>

                        <!-- Error -->
                        <div v-else-if="loadError" class="bl-state bl-state--error" role="alert">
                            <p class="bl-state-title">Couldn't load bids.</p>
                            <p class="bl-state-body">Check your connection and try again.</p>
                            <div class="bl-state-actions">
                                <button type="button" class="bl-btn solid" @click="fetchData(currentPage)">Retry</button>
                            </div>
                        </div>

                        <!-- Empty -->
                        <div v-else-if="!processedFiles.length" class="bl-state">
                            <template v-if="appliedSearch">
                                <p class="bl-state-title">No bids match "{{ appliedSearch }}".</p>
                                <div class="bl-state-actions">
                                    <button type="button" class="bl-btn outline" @click="clearSearch">Clear Search</button>
                                </div>
                            </template>
                            <template v-else-if="selectedCategory && activeTab !== 'PENDING'">
                                <p class="bl-state-title">No bids in this category.</p>
                                <div class="bl-state-actions">
                                    <button type="button" class="bl-btn outline" @click="selectedCategory = ''">Show All Categories</button>
                                </div>
                            </template>
                            <template v-else-if="activeTab === 'PENDING'">
                                <p class="bl-state-title">Nothing to upload.</p>
                                <p class="bl-state-body">Choose Go on a bookmarked tender.</p>
                                <div class="bl-state-actions">
                                    <router-link :to="{ name: 'Bookmarks' }" class="bl-btn solid">Go to Bookmarks</router-link>
                                </div>
                            </template>
                            <template v-else-if="activeTab === 'NEW'">
                                <p class="bl-state-title">No open bids yet.</p>
                                <p class="bl-state-body">Bookmark a tender in Tender Search, then choose Go.</p>
                                <div class="bl-state-actions">
                                    <router-link :to="{ name: 'Tenders' }" class="bl-btn solid">Go to Tender Search</router-link>
                                    <button v-if="isAdminUser" type="button" class="bl-btn outline" @click="fileUploadPage">Upload PDFs</button>
                                </div>
                            </template>
                            <p v-else class="bl-state-title">{{ emptyTitle }}</p>
                        </div>

                        <!-- Grid -->
                        <div v-else-if="viewMode === 'grid'" class="bl-grid">
                            <div class="bl-card" v-for="pdf in processedFiles" :key="pdf.id" @click="openRow(pdf)">
                                <!-- Card top: icon + status -->
                                <div class="bl-card-top">
                                    <div class="bl-card-icon">
                                        <img src="@/assets/pdf_icon.png" alt="" width="26" height="26"
                                            style="object-fit: contain;">
                                    </div>
                                    <span class="bl-status" :class="pillFor(pdf).cls" :title="pillFor(pdf).hint">
                                        <span v-if="pillFor(pdf).spinning" class="bl-spinner sm" aria-hidden="true"></span>
                                        {{ pillFor(pdf).label }}
                                        <span v-if="pillFor(pdf).cls === 'failed'" class="sr-only">. {{ pillFor(pdf).hint }}</span>
                                    </span>
                                </div>

                                <!-- Name -->
                                <h3 class="bl-card-name" :title="stripExtension(pdf.project_name)">
                                    {{ stripExtension(pdf.project_name) }}
                                </h3>

                                <!-- Meta -->
                                <div class="bl-card-meta">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                        stroke-width="2" aria-hidden="true">
                                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                        <line x1="16" y1="2" x2="16" y2="6" />
                                        <line x1="8" y1="2" x2="8" y2="6" />
                                        <line x1="3" y1="10" x2="21" y2="10" />
                                    </svg>
                                    <span><span class="sr-only">Last submission date: </span>{{ formatDate(pdf.last_submission_date) }}</span>
                                </div>

                                <!-- Footer actions -->
                                <div class="bl-card-footer">
                                    <template v-if="pdf.is_bookmark">
                                        <button v-if="isAdminUser" type="button" class="bl-btn primary" @click.stop="uploadDocuments(pdf)">
                                            Upload PDFs
                                        </button>
                                        <button type="button" class="bl-btn outline" @click.stop="openTender(pdf)">
                                            View Tender
                                        </button>
                                    </template>
                                    <template v-else>
                                        <button type="button" class="bl-btn primary" @click.stop="summaryListingPage(pdf)">
                                            View Bid Details
                                        </button>
                                        <button v-if="quickActionFor(pdf)" type="button" class="bl-btn outline"
                                            :disabled="updatingStatusIds.includes(pdf.id)"
                                            :aria-label="quickActionFor(pdf).label + ': ' + stripExtension(pdf.project_name)"
                                            @click.stop="runQuickAction(pdf)">
                                            <span v-if="updatingStatusIds.includes(pdf.id)" class="bl-spinner sm" aria-hidden="true"></span>
                                            {{ quickActionFor(pdf).label }}
                                        </button>
                                    </template>
                                </div>
                            </div>
                        </div>

                        <!-- Table -->
                        <div v-else class="bl-table-container">
                            <table class="bl-table">
                                <colgroup>
                                    <col class="col-name">
                                    <col class="col-date">
                                    <col class="col-status">
                                    <col class="col-actions">
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th :aria-sort="nameSort">
                                            <button type="button" class="th-sort" @click="toggleSort('name')">
                                                {{ activeTab === 'PENDING' ? 'Tender' : 'Bid Name' }}
                                                <span class="sort-arrow" aria-hidden="true" v-if="sortBy === 'name_asc'">▲</span>
                                                <span class="sort-arrow" aria-hidden="true" v-else-if="sortBy === 'name_desc'">▼</span>
                                            </button>
                                        </th>
                                        <th class="th-date" :aria-sort="dateSort">
                                            <button type="button" class="th-sort" @click="toggleSort('date')">
                                                Last Submission Date
                                                <span class="sort-arrow" aria-hidden="true" v-if="sortBy === 'oldest'">▲</span>
                                                <span class="sort-arrow" aria-hidden="true" v-else-if="sortBy === 'newest'">▼</span>
                                            </button>
                                        </th>
                                        <th class="th-status">Status</th>
                                        <th class="th-activity">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="pdf in processedFiles" :key="pdf.id" @click="openRow(pdf)">
                                        <td>
                                            <div class="file-info-cell">
                                                <div class="bl-table-icon">
                                                    <img src="@/assets/pdf_icon.png" alt="" width="20" height="20">
                                                </div>
                                                <!-- A real button so each row is reachable by keyboard;
                                                     the row click stays as the larger mouse target. -->
                                                <button type="button" class="file-name-text" :title="stripExtension(pdf.project_name)"
                                                    @click.stop="openRow(pdf)">
                                                    {{ truncateName(pdf.project_name) }}
                                                </button>
                                            </div>
                                        </td>
                                        <td>
                                            <span class="text-date">{{ formatDate(pdf.last_submission_date) }}</span>
                                        </td>
                                        <td>
                                            <span class="bl-status" :class="pillFor(pdf).cls" :title="pillFor(pdf).hint">
                                                <span v-if="pillFor(pdf).spinning" class="bl-spinner sm" aria-hidden="true"></span>
                                                {{ pillFor(pdf).label }}
                                                <span v-if="pillFor(pdf).cls === 'failed'" class="sr-only">. {{ pillFor(pdf).hint }}</span>
                                            </span>
                                        </td>
                                        <td>
                                            <div class="action-buttons">
                                                <template v-if="pdf.is_bookmark">
                                                    <button v-if="isAdminUser" type="button" class="bl-btn primary sm" @click.stop="uploadDocuments(pdf)">
                                                        Upload PDFs
                                                    </button>
                                                    <button type="button" class="bl-btn outline sm" @click.stop="openTender(pdf)">
                                                        View Tender
                                                    </button>
                                                </template>
                                                <template v-else>
                                                    <button v-if="quickActionFor(pdf)" type="button" class="bl-btn outline sm"
                                                        :disabled="updatingStatusIds.includes(pdf.id)"
                                                        :aria-label="quickActionFor(pdf).label + ': ' + stripExtension(pdf.project_name)"
                                                        @click.stop="runQuickAction(pdf)">
                                                        <span v-if="updatingStatusIds.includes(pdf.id)" class="bl-spinner sm" aria-hidden="true"></span>
                                                        {{ quickActionFor(pdf).label }}
                                                    </button>
                                                    <button type="button" class="bl-btn primary sm" @click.stop="summaryListingPage(pdf)">
                                                        View Bid Details
                                                    </button>
                                                </template>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <AppPagination
                            v-if="!loading && !loadError && processedFiles.length > 0"
                            :currentPage="currentPage"
                            :totalPages="totalPages"
                            @page-changed="fetchData"
                        />
                    </div>

                </div>
            </section>
        </main>
    </div>
</template>

<script>
import axios from 'axios';
import AppSidebar from './Sidebar.vue';
import AppPagination from './AppPagination.vue';
import { isAdmin } from '@/utils/auth';
import { notify } from '@/utils/notify';
import { confirmAction } from '@/utils/confirm';
import { STATUS_LABEL, STATUS_HINT, effectiveStatus, actionsFor } from '@/utils/bidStatus';

// Tab keys are shared with other pages (they link here with ?tab=KEY).
const TABS = [
    { key: 'NEW', label: 'Open' },
    { key: 'EXPIRED', label: 'Expired' },
    { key: 'PENDING', label: 'To Upload' },
];
const DEFAULT_TAB = 'NEW';
const PENDING_HINT = "Bookmarks you're preparing. Upload their PDFs to start the analysis.";
const EMPTY_TITLE = {
    EXPIRED: 'No expired bids.',
};

const PAGE_SIZE = 10;
const POLL_MS = 30 * 1000;
// A bid not marked DONE after this long is treated as ready: legacy rows have
// no summary_status, and a stuck job shouldn't spin forever.
const ANALYSIS_WINDOW_MS = 2 * 60 * 60 * 1000;

function tabFromQuery(value) {
    const key = String(value || '').toUpperCase();
    return TABS.some(t => t.key === key) ? key : DEFAULT_TAB;
}

export default {
    name: 'BidListing',

    components: {
        AppSidebar,
        AppPagination
    },

    props: {
        getAccessToken: Function,
    },

    data() {
        return {
            tabs: TABS,
            apiResponseData: [],
            searchQuery: "",
            appliedSearch: "",     // searchQuery after the debounce
            searchTimer: null,
            activeTab: tabFromQuery(this.$route.query.tab),
            viewMode: "list", // 'grid' | 'list'
            sortBy: "newest",  // 'newest' | 'oldest' | 'name_asc' | 'name_desc'
            selectedCategory: "", // '' = All Categories, or category id
            categories: [], // Loaded from /get-pdf-category
            currentPage: 1,
            totalPages: 1,
            updatingStatusIds: [],
            loading: false,
            loadError: false,
            requestSeq: 0,
            pollTimer: null,
            isAdminUser: isAdmin(),
        }
    },

    mounted() {
        this.loadProfile();
        this.fetchCategories();
        this.fetchData(1);
    },

    watch: {
        '$route.query.tab'(value) {
            const tab = tabFromQuery(value);
            if (tab !== this.activeTab) this.activeTab = tab;
        },
        activeTab(tab) {
            if (this.$route.query.tab !== tab) {
                this.$router.replace({ query: { ...this.$route.query, tab } });
            }
            this.apiResponseData = [];
            this.fetchData(1);
        },
        searchQuery(value) {
            clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(() => {
                this.appliedSearch = value.trim();
            }, 300);
        },
        appliedSearch() {
            this.fetchData(1);
        },
        sortBy() {
            this.fetchData(1);
        },
        selectedCategory() {
            this.fetchData(1);
        }
    },

    beforeUnmount() {
        clearTimeout(this.searchTimer);
        this.stopPolling();
    },

    computed: {
        processedFiles() {
            // Tab filtering, search and sorting happen on the backend (bids)
            // or in fetchData (bookmarks), so pagination stays correct.
            return this.apiResponseData || [];
        },
        activeHint() {
            return this.activeTab === 'PENDING' ? PENDING_HINT : (STATUS_HINT[this.activeTab] || '');
        },
        emptyTitle() {
            return EMPTY_TITLE[this.activeTab] || 'No bids.';
        },
        nameSort() {
            if (this.sortBy === 'name_asc') return 'ascending';
            if (this.sortBy === 'name_desc') return 'descending';
            return null;
        },
        dateSort() {
            if (this.sortBy === 'oldest') return 'ascending';
            if (this.sortBy === 'newest') return 'descending';
            return null;
        },
    },

    methods: {

        loadProfile: function () {
            const url = this.$API_URL + "/profile";
            axios.get(url, {
                headers: {
                    'Authorization': 'Bearer ' + this.getAccessToken()
                }
            }).catch((error) => {
                console.error("Profile fetch error", error);
            });
        },

        // silent: refresh in place (polling) without the loading state.
        async fetchData(page = 1, { silent = false } = {}) {
            this.currentPage = page;
            const seq = ++this.requestSeq;
            if (!silent) {
                this.loading = true;
                this.loadError = false;
            }
            const headers = { 'Authorization': 'Bearer ' + this.getAccessToken() };
            const status = this.activeTab;
            const search = this.appliedSearch;

            try {
                if (status === 'PENDING') {
                    // To Upload = bookmarks marked as being prepared.
                    const response = await axios.get(this.$API_URL + '/bookmarks', { headers });
                    if (seq !== this.requestSeq) return;
                    let bms = (response.data.bookmarks || [])
                        .filter(b => b.project_status === 'active')
                        .map(b => ({
                            id: b.tender_id,
                            pdf_name: b.title,
                            project_name: b.title,
                            last_submission_date: b.closing_date,
                            is_bookmark: true
                        }));

                    if (search) {
                        const lowerSearch = search.toLowerCase();
                        bms = bms.filter(b => (b.project_name && b.project_name.toLowerCase().includes(lowerSearch)));
                    }
                    bms.sort(this.compareBookmarks);

                    this.totalPages = Math.ceil(bms.length / PAGE_SIZE) || 1;
                    const start = (page - 1) * PAGE_SIZE;
                    this.apiResponseData = bms.slice(start, start + PAGE_SIZE);
                } else {
                    // Lightweight listing endpoint: only the fields this page renders.
                    let url = this.$API_URL + `/user/get-pdf-list?page=${page}&page_size=${PAGE_SIZE}&status=${status}&search=${encodeURIComponent(search)}&sort_by=${this.sortBy}`;
                    if (this.selectedCategory) {
                        url += `&category=${encodeURIComponent(this.selectedCategory)}`;
                    }
                    const response = await axios.get(url, { headers });
                    if (seq !== this.requestSeq) return;
                    this.apiResponseData = Array.isArray(response.data) ? response.data : (response.data.items || []);
                    this.totalPages = response.data.total_pages || 1;
                }
                this.loadError = false;
            } catch (error) {
                if (seq !== this.requestSeq) return;
                console.error('Failed to load bids', error);
                if (error.response && error.response.status === 401) {
                    localStorage.removeItem('user-info');
                    this.$router.push({ name: 'LoginPage' });
                    return;
                }
                // A failed background refresh keeps the rows already shown.
                if (!silent) this.loadError = true;
            } finally {
                if (seq === this.requestSeq) {
                    this.loading = false;
                    this.updatePolling();
                }
            }
        },

        compareBookmarks(a, b) {
            const name = (x) => (x.project_name || '').toLowerCase();
            const time = (x) => (x.last_submission_date ? new Date(x.last_submission_date).getTime() : 0);
            if (this.sortBy === 'name_asc') return name(a).localeCompare(name(b));
            if (this.sortBy === 'name_desc') return name(b).localeCompare(name(a));
            if (this.sortBy === 'oldest') return time(a) - time(b);
            return time(b) - time(a);
        },

        fetchCategories: function () {
            const url = this.$API_URL + "/user/get-pdf-category";
            axios.get(url, {
                headers: {
                    'Authorization': 'Bearer ' + this.getAccessToken()
                }
            })
                .then((response) => {
                    this.categories = response.data || [];
                })
                .catch((error) => {
                    console.error("Error fetching categories:", error);
                });
        },

        // ── Tabs ────────────────────────────────────────────────────────
        selectTab(key) {
            this.activeTab = key;
        },

        // Arrow keys move between tabs (WAI-ARIA tabs pattern).
        onTabKeydown(event, index) {
            const last = this.tabs.length - 1;
            let next = null;
            if (event.key === 'ArrowRight') next = index === last ? 0 : index + 1;
            else if (event.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
            else if (event.key === 'Home') next = 0;
            else if (event.key === 'End') next = last;
            if (next === null) return;
            event.preventDefault();
            const key = this.tabs[next].key;
            this.selectTab(key);
            this.$nextTick(() => {
                const el = document.getElementById('bl-tab-' + key);
                if (el) el.focus();
            });
        },

        clearSearch() {
            clearTimeout(this.searchTimer);
            this.searchQuery = '';
            this.appliedSearch = '';
        },

        // ── Analysis state ──────────────────────────────────────────────
        // 'ready' | 'analysing' | 'failed', from the backend's summary_status.
        analysisState(pdf) {
            if (!pdf || pdf.is_bookmark) return 'ready';
            const s = String(pdf.summary_status || '').toUpperCase();
            if (s === 'DONE') return 'ready';
            if (s === 'FAILED') return 'failed';
            // The server decides with the DB's own clock; comparing the naive
            // created_date here was off by the UTC/IST offset.
            if (typeof pdf.is_processing === 'boolean') return pdf.is_processing ? 'analysing' : 'ready';
            const created = pdf.created_date ? new Date(pdf.created_date).getTime() : NaN;
            if (isNaN(created) || Date.now() - created > ANALYSIS_WINDOW_MS) return 'ready';
            return 'analysing';
        },

        pillFor(pdf) {
            if (pdf.is_bookmark) {
                return { label: 'To Upload', cls: 'pending', hint: 'PDFs not uploaded yet.' };
            }
            const state = this.analysisState(pdf);
            if (state === 'failed') {
                return { label: 'Analysis Failed', cls: 'failed', hint: 'Ask an admin to reprocess it from the Admin Panel.' };
            }
            if (state === 'analysing') {
                return {
                    label: 'Analysing…',
                    cls: 'analysing',
                    hint: 'The AI is still reading the PDFs. This takes about 10–15 minutes; you can open the bid meanwhile.',
                    spinning: true
                };
            }
            const status = effectiveStatus(pdf);
            return { label: STATUS_LABEL[status] || status, cls: status.toLowerCase(), hint: STATUS_HINT[status] || '' };
        },

        updatePolling() {
            const needed = !this.loadError && this.activeTab !== 'PENDING'
                && this.apiResponseData.some(p => this.analysisState(p) === 'analysing');
            if (needed && !this.pollTimer) {
                this.pollTimer = setInterval(() => {
                    if (document.hidden || this.loading) return;
                    this.fetchData(this.currentPage, { silent: true });
                }, POLL_MS);
            } else if (!needed) {
                this.stopPolling();
            }
        },

        stopPolling() {
            if (this.pollTimer) {
                clearInterval(this.pollTimer);
                this.pollTimer = null;
            }
        },

        // ── Navigation ──────────────────────────────────────────────────
        openRow(pdf) {
            if (pdf.is_bookmark) this.openTender(pdf);
            else this.summaryListingPage(pdf);
        },

        openTender(pdf) {
            this.$router.push(`/tender-search/${pdf.id}`);
        },

        uploadDocuments(pdf) {
            this.$router.push({
                name: 'Home',
                query: {
                    tender_id: pdf.id,
                    tender_name: pdf.project_name || ''
                }
            });
        },

        fileUploadPage: function () {
            this.$router.push({ name: 'Home' });
        },

        summaryListingPage: function (pdf) {
            // Pass the already-loaded bid row so the details page can render instantly
            // without depending on the (buggy) single-bid endpoint.
            try {
                sessionStorage.setItem('bid-detail-' + pdf.id, JSON.stringify(pdf));
            } catch (e) { /* ignore storage errors */ }

            this.$router.push({
                name: 'BidDetails',
                params: { pdf_id: pdf.id },
                query: { status: this.activeTab }
            });
        },

        // ── Status quick action (admin only) ────────────────────────────
        quickActionFor(pdf) {
            if (!this.isAdminUser || pdf.is_bookmark) return null;
            const actions = actionsFor(pdf);
            return actions.find(a => a.primary) || actions[0] || null;
        },

        async runQuickAction(pdf) {
            const action = this.quickActionFor(pdf);
            if (!action || this.updatingStatusIds.includes(pdf.id)) return;
            if (action.confirm) {
                const ok = await confirmAction(action.confirm);
                if (!ok) return;
            }
            this.updatingStatusIds.push(pdf.id);
            try {
                await axios.post(this.$API_URL + `/user/start-project?pdf_id=${pdf.id}&status=${action.to}`, null, {
                    headers: { 'Authorization': 'Bearer ' + this.getAccessToken() }
                });
                notify.success(action.success);
                await this.fetchData(this.currentPage, { silent: true });
                // The row may have left this tab; step back if the page emptied.
                if (!this.apiResponseData.length && this.currentPage > 1) {
                    await this.fetchData(this.currentPage - 1);
                }
            } catch (e) {
                const detail = e && e.response && e.response.data && e.response.data.detail;
                notify.error(typeof detail === 'string' && detail
                    ? detail
                    : "Couldn't update the bid. Check your connection and try again.");
            } finally {
                this.updatingStatusIds = this.updatingStatusIds.filter(id => id !== pdf.id);
            }
        },

        // ── Formatting ──────────────────────────────────────────────────
        stripExtension(name) {
            if (!name) return 'Untitled Bid';
            // Remove every .pdf (handles comma-joined multi-file names too)
            return name.replace(/\.pdf/gi, '');
        },
        truncateName(name, limit = 50) {
            const clean = this.stripExtension(name);
            return clean.length > limit ? clean.slice(0, limit) + '…' : clean;
        },

        formatDate(dateString) {
            if (!dateString) return '—';
            const date = new Date(dateString);
            if (isNaN(date)) return '—';
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        },

        toggleSort(column) {
            if (column === 'name') {
                this.sortBy = (this.sortBy === 'name_asc') ? 'name_desc' : 'name_asc';
            } else if (column === 'date') {
                this.sortBy = (this.sortBy === 'newest') ? 'oldest' : 'newest';
            }
        }
    }
}
</script>

<style scoped>
/* ============================================================
   Bids listing — typography & layout
   ============================================================ */
.file-list-wrapper {
    font-family: var(--font-sans);
}

/* ===== Header ===== */
.bl-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 1.25rem;
}

.bl-heading { min-width: 0; }

.bl-title {
    font-family: var(--font-display);   /* Outfit — page title */
    font-size: var(--fs-h1);            /* 36px page title */
    line-height: var(--lh-h1);          /* 44px */
    font-weight: var(--fw-h1);          /* 700 */
    color: var(--text-primary);
    margin: 0;
    letter-spacing: -0.02em;
}

/* White on #0071C5 (5.1:1); hover #005a9e. */
.bl-upload-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: #0071C5;
    color: #fff;
    border: none;
    padding: 0.7rem 1.3rem;
    border-radius: 10px;
    font-family: var(--font-sans);
    font-size: 0.9rem;
    font-weight: 700;
    cursor: pointer;
    box-shadow: 0 6px 16px rgba(0, 113, 197, 0.25);
    transition: background 0.2s, box-shadow 0.2s;
    flex-shrink: 0;
}

.bl-upload-btn:hover {
    background: #005a9e;
    box-shadow: 0 10px 22px rgba(0, 113, 197, 0.32);
}

/* ===== Tabs ===== */
.bl-tabs-block {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    min-width: 0;
    margin-bottom: 1.25rem;
}

.bl-tabs {
    display: inline-flex;
    align-self: flex-start;
    flex-wrap: wrap;
    gap: 4px;
    padding: 4px;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 999px;
    max-width: 100%;
}

.bl-tab {
    padding: 0.5rem 1.1rem;
    border: none;
    border-radius: 999px;
    background: transparent;
    color: var(--text-secondary, #475569);
    font-family: var(--font-sans);
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s, color 0.2s, box-shadow 0.2s;
    white-space: nowrap;
}

.bl-tab:hover {
    color: #0f172a;
    background: rgba(255, 255, 255, 0.7);
}

/* Selected: white surface + weight + accent text, not color alone. */
.bl-tab.active {
    background: #ffffff;
    color: #0071C5;
    font-weight: 700;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.12);
}

.bl-tab:focus-visible,
.btn-toggle:focus-visible,
.bl-btn:focus-visible,
.bl-upload-btn:focus-visible,
.th-sort:focus-visible,
.file-name-text:focus-visible {
    outline: 2px solid #0071C5;
    outline-offset: 2px;
}

.bl-tab-hint {
    margin: 0;
    padding-left: 0.25rem;
    font-size: 0.875rem;
    line-height: 1.4;
    color: var(--text-muted, #64748b);
}

/* ===== Search / sort bar ===== */
.search-sort-bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
}

.search-container {
    flex: 1;
    min-width: 250px;
}

.controls-container {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 1.5rem;
}

.sort-wrapper {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.sort-select, .category-select {
    padding: 0.5rem 2rem 0.5rem 1rem;
    border-radius: 10px;
    border: 1px solid #e2e8f0;
    background-color: #fff;
    color: #1e293b;
    font-family: inherit;
    outline: none;
    cursor: pointer;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%231e293b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 0.5rem center;
    background-size: 1rem;
}

.sort-select:focus-visible, .category-select:focus-visible {
    border-color: #0071C5;
    box-shadow: 0 0 0 3px rgba(0, 113, 197, 0.2);
}

.view-toggles {
    display: flex;
    background: #f1f5f9;
    border-radius: 10px;
    padding: 4px;
    border: 1px solid #e2e8f0;
    gap: 4px;
}

.btn-toggle {
    background: transparent;
    border: none;
    color: #64748b;
    padding: 6px;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.2s, color 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
}

.btn-toggle:hover {
    color: #1e293b;
    background: rgba(0, 0, 0, 0.05);
}

.btn-toggle.active {
    background: #0071C5;
    color: #fff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* ===== Grid cards ===== */
.bl-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(265px, 1fr));
    gap: 1.25rem;
    margin-top: 0.5rem;
}

.bl-card {
    position: relative;
    background: #fff;
    border: 1px solid #eef2f7;
    border-radius: 16px;
    padding: 1.25rem;
    display: flex;
    flex-direction: column;
    min-height: 190px;
    cursor: pointer;
    transition: transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease;
    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.04);
    overflow: hidden;
}

.bl-card:hover {
    transform: translateY(-4px);
    border-color: rgba(0, 113, 197, 0.35);
    box-shadow: 0 14px 30px rgba(0, 113, 197, 0.14);
}

.bl-card-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    margin-bottom: 1rem;
}

.bl-card-icon {
    width: 46px;
    height: 46px;
    border-radius: 12px;
    background: rgba(239, 68, 68, 0.08);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.bl-card-name {
    font-size: 1rem;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 0.6rem;
    line-height: 1.4;
    letter-spacing: -0.01em;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-word;
}

.bl-card-meta {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    font-size: 0.8rem;
    font-weight: 500;
    color: #64748b;
    margin-bottom: 1rem;
}

.bl-card-footer {
    display: flex;
    gap: 0.5rem;
    margin-top: auto;
    padding-top: 0.85rem;
    border-top: 1px dashed #e2e8f0;
    flex-wrap: wrap;
}

/* ===== Status pill (shared grid + table) =====
   Text carries the status; the tint only reinforces it. All pairs ≥ 4.5:1. */
.bl-status {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.75rem;
    font-weight: 700;
    padding: 0.28rem 0.7rem;
    border-radius: 999px;
    white-space: nowrap;
    background: #f1f5f9;
    color: #475569;
}

.bl-status.new { background: #eff6ff; color: #005a9e; }
.bl-status.expired { background: #fffbeb; color: #92400e; }
.bl-status.win,
.bl-status.completed { background: #ecfdf5; color: #047857; }
.bl-status.pending { background: #f1f5f9; color: #475569; }
.bl-status.analysing { background: #f0f9ff; color: #0369a1; }
.bl-status.failed { background: #fef3f2; color: #b42318; }

/* Small activity indicator; stops under reduced motion. */
.bl-spinner {
    display: inline-block;
    width: 16px;
    height: 16px;
    border: 2px solid currentColor;
    border-right-color: transparent;
    border-radius: 50%;
    animation: bl-spin 0.9s linear infinite;
    flex-shrink: 0;
}

.bl-spinner.sm {
    width: 10px;
    height: 10px;
    border-width: 2px;
}

@keyframes bl-spin {
    to { transform: rotate(360deg); }
}

/* ===== Shared buttons ===== */
.bl-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
    border: 1px solid transparent;
    border-radius: 10px;
    padding: 0.55rem 1rem;
    font-family: var(--font-sans);
    font-size: 0.82rem;
    font-weight: 700;
    line-height: 1;
    cursor: pointer;
    transition: background 0.2s, color 0.2s, border-color 0.2s;
    white-space: nowrap;
    text-decoration: none;
}

.bl-btn.sm {
    padding: 0.45rem 0.85rem;
    font-size: 0.78rem;
}

/* #0071C5 on its 10% tint is 4.6:1; hover is white on #0071C5. */
.bl-btn.primary { background: rgba(0, 113, 197, 0.1); color: #005a9e; }
.bl-btn.primary:hover:not(:disabled) { background: #0071C5; color: #fff; }

.bl-btn.solid { background: #0071C5; color: #fff; }
.bl-btn.solid:hover:not(:disabled) { background: #005a9e; color: #fff; }

.bl-btn.outline { background: #fff; color: #334155; border-color: #cbd5e1; }
.bl-btn.outline:hover:not(:disabled) { border-color: #0071C5; color: #005a9e; }

.bl-btn:disabled { opacity: 0.6; cursor: not-allowed; }

/* ===== Table ===== */
.bl-table-container {
    width: 100%;
    overflow-x: auto;
    background: #fff;
    border: 1px solid #eef2f7;
    border-radius: 16px;
    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.04);
}

.bl-table {
    width: 100%;
    border-collapse: collapse;
    min-width: 640px;
    font-family: var(--font-sans);
    table-layout: fixed;
}

/* Fixed widths: without them the name column eats the row and the date,
   status and action columns drift away from their headers. */
.col-name { width: auto; }
.col-date { width: 220px; }
.col-status { width: 140px; }
.col-actions { width: 180px; }

.bl-table th {
    text-align: left;
    vertical-align: middle;
    padding: 0.75rem 1.5rem;
    color: #475569;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    border-bottom: 2px solid #e2e8f0;
    background: #f1f5f9;
    user-select: none;
    white-space: nowrap;
}

/* Each column's header and its cells share one alignment. */
.bl-table th.th-date,
.bl-table td:nth-child(2) { text-align: right; }

.bl-table th.th-status,
.bl-table td:nth-child(3) { text-align: center; }

.bl-table th.th-activity,
.bl-table td:nth-child(4) { text-align: right; }

.th-sort {
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    color: inherit;
    letter-spacing: inherit;
    text-transform: inherit;
    cursor: pointer;
    border-radius: 6px;
    transition: color 0.2s;
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
}

.th-sort:hover { color: #0071C5; }
.sort-arrow { font-size: 0.65rem; }

.bl-table td {
    padding: 0.9rem 1.5rem;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
    color: #1e293b;
    font-size: 0.88rem;
    font-weight: 500;
}

.bl-table tbody tr { transition: background 0.18s; cursor: pointer; }
.bl-table tbody tr:nth-child(even) { background: #fafbfc; }
.bl-table tbody tr:hover { background: #eff6ff; }
.bl-table tbody tr:last-child td { border-bottom: none; }

.file-info-cell {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    min-width: 0;
}

.bl-table-icon {
    width: 36px;
    height: 36px;
    border-radius: 6px;
    background: rgba(239, 68, 68, 0.08);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.file-name-text {
    background: none;
    border: none;
    padding: 0;
    text-align: left;
    font-family: inherit;
    font-weight: 600;
    font-size: 0.9rem;
    color: #0f172a;
    cursor: pointer;
    border-radius: 6px;
    transition: color 0.2s;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.file-name-text:hover { color: #0071C5; }

.text-date {
    color: #64748b;
    font-size: 0.85rem;
    font-weight: 500;
}

.action-buttons {
    display: flex;
    gap: 0.5rem;
    justify-content: flex-end;
    align-items: center;
}

/* ===== Loading / empty / error ===== */
.bl-loading-text {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0 0 0.75rem;
    font-size: 0.875rem;
    font-weight: 600;
    color: #475569;
}

.bl-skeleton {
    background: #fff;
    border: 1px solid #eef2f7;
    border-radius: 16px;
    overflow: hidden;
}

.bl-skeleton-row {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid #f4f6fb;
}

.bl-skeleton-row:last-child { border-bottom: none; }

.bl-skeleton-icon {
    width: 36px;
    height: 36px;
    border-radius: 6px;
    background: #f1f5f9;
    flex-shrink: 0;
}

.bl-skeleton-bar {
    height: 12px;
    width: 18%;
    border-radius: 6px;
    background: #f1f5f9;
}

.bl-skeleton-bar.wide { width: 40%; }
.bl-skeleton-bar.short { width: 10%; margin-left: auto; }

.bl-state {
    background: #fff;
    border: 1px solid #eef2f7;
    border-radius: 16px;
    padding: 3rem 1.5rem;
    text-align: center;
}

.bl-state--error {
    border-color: #fecdca;
    background: #fffbfa;
}

.bl-state-title {
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
    color: #0f172a;
}

.bl-state--error .bl-state-title { color: #b42318; }

.bl-state-body {
    margin: 0.4rem 0 0;
    font-size: 0.9rem;
    color: #475569;
}

.bl-state-actions {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 0.6rem;
    margin-top: 1.25rem;
}

@media (prefers-reduced-motion: reduce) {
    .bl-spinner { animation: none; border-right-color: currentColor; opacity: 0.6; }
    .bl-card,
    .bl-card:hover { transition: none; transform: none; }
}
</style>
